import neo4j from 'neo4j-driver';
import logger from '../utils/logger';

let driver = null;
let initPromise = null;
let isClosing = false;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const getDriver = () => {
  if (!driver) {
    const uri = process.env.REACT_APP_NEO4J_URI;
    const user = process.env.REACT_APP_NEO4J_USER;
    const password = process.env.REACT_APP_NEO4J_PASSWORD;

    if (!uri || !user || !password) {
      logger.error('Missing Neo4j configuration');
      throw new Error('Missing Neo4j configuration');
    }

    try {
      driver = neo4j.driver(
        uri,
        neo4j.auth.basic(user, password),
        {
          maxConnectionPoolSize: 100,
          logging: {
            level: 'info',
            logger: (level, message) => {
              switch (level) {
                case 'error':
                  logger.error(message);
                  break;
                case 'warn':
                  logger.warn(message);
                  break;
                case 'info':
                  logger.info(message);
                  break;
                default:
                  logger.debug(message);
              }
            }
          }
        }
      );
    } catch (error) {
      logger.error('Failed to create Neo4j driver:', error);
      throw error;
    }
  }
  return driver;
};

export const getSession = () => {
  const driver = getDriver();
  return driver.session({
    database: process.env.REACT_APP_NEO4J_DATABASE || 'neo4j'
  });
};

export const initNeo4j = async (retryCount = 3) => {
  if (isClosing) {
    logger.info('Waiting for previous connection to close...');
    await wait(1000);
    isClosing = false;
  }

  if (!initPromise) {
    initPromise = (async () => {
      try {
        if (!driver) {
          logger.info('Creating new Neo4j driver...');
          driver = getDriver();
        }

        let lastError = null;
        for (let i = 0; i < retryCount; i++) {
          try {
            logger.info(`Attempting to verify connectivity (attempt ${i + 1}/${retryCount})...`);
            await driver.verifyConnectivity();
            logger.info('Successfully connected to Neo4j');

            const session = getSession();
            try {
              // Create root node with timestamp
              await session.run(`
                MERGE (root:SmartEcoCard {id: 'root'})
                ON CREATE SET root.createdAt = timestamp()
                RETURN root
              `);
              logger.info('Root node created/verified successfully');

              // Create constraints
              await session.run(`
                CREATE CONSTRAINT user_id IF NOT EXISTS
                FOR (u:User) REQUIRE u.userId IS UNIQUE
              `);

              // Create constraint for root node
              await session.run(`
                CREATE CONSTRAINT smartecocard_id IF NOT EXISTS
                FOR (s:SmartEcoCard) REQUIRE s.id IS UNIQUE
              `);
              logger.info('Neo4j constraints created successfully');
            } finally {
              await session.close();
            }

            return driver;
          } catch (error) {
            lastError = error;
            logger.error(`Connection attempt ${i + 1} failed:`, error.message);

            if (driver) {
              try {
                await driver.close();
              } catch (closeError) {
                logger.error('Error closing driver:', closeError);
              }
            }

            if (i < retryCount - 1) {
              logger.info('Waiting before retry...');
              await wait(2000);
              driver = getDriver();
            }
          }
        }

        throw lastError || new Error('Failed to connect to Neo4j after multiple attempts');
      } catch (error) {
        logger.error('Neo4j initialization failed:', error);
        driver = null;
        initPromise = null;
        throw error;
      }
    })();
  }

  return initPromise;
};

export const closeNeo4j = async () => {
  isClosing = true;
  if (driver) {
    try {
      logger.info('Closing Neo4j connection...');
      await driver.close();
      logger.info('Neo4j connection closed successfully');
    } catch (error) {
      logger.error('Error closing Neo4j connection:', error);
    } finally {
      driver = null;
      initPromise = null;
      isClosing = false;
    }
  }
};

export const checkConnection = async () => {
  const session = getSession();
  try {
    const result = await session.run('RETURN 1 as num');
    return result.records[0].get('num') === 1;
  } catch (error) {
    logger.error('Neo4j connection check failed:', error);
    return false;
  } finally {
    await session.close();
  }
};

export default {
  initNeo4j,
  getDriver,
  getSession,
  closeNeo4j,
  checkConnection
};
